import store from "@/store";
import { reactive, ref, onBeforeMount } from "@vue/composition-api";
import useFlatPickr from "@/utils/hooks/useFlatPickr";
import useValidationModal from "@/@core/components/base-modal/hooks/useValidationModal";
import router from "@/router";

import { articleCategoryModel } from "@/models/article/article";

export default function useArticleCategoryForm(props) {
  const {
    fetchArticleCategoryDetail,
    createArticleCategoryModel,
    editArticleCategoryModel,
  } = articleCategoryModel();
  const { openSuccessModal, openDefaultErrorModal } = useValidationModal();
  const loading = reactive({
    api: false,
    createOrEdit: false,
  });

  const formData = reactive({
    seq: null,
    category_name_th: null,
    category_name_en: null,
    valid_from: { date: null, time: null },
    valid_through: { date: null, time: null },
    category_image_active: { file: null, url: null },
    category_image_inactive: { file: null, url: null },
    status: false,
  });

  const {
    validFrom,
    validThrough,
    flatpickrDateConfig,
    flatpickrTimeConfig,
    onOpenValidFromDatePicker,
    onOpenValidFromTimePicker,
    onOpenValidThroughTimePicker,
    onSelectValidFromDate,
    onSelectValidFromTimePicker,
  } = useFlatPickr();

  const onUploadImageActive = (image) => {
    formData.category_image_active.file = image.file;
    formData.category_image_active.url = image.url;
  };

  const onUploadImageInactive = (image) => {
    formData.category_image_inactive.file = image.file;
    formData.category_image_inactive.url = image.url;
  };

  const resolveDate = (dateInput) => {
    const date = new Date(dateInput);

    try {
      const dateStr = `${date.toLocaleString("default", {
        day: "2-digit",
      })}-${date.toLocaleString("default", {
        month: "2-digit",
      })}-${date.getFullYear()}`;

      return dateStr;
    } catch (err) {
      return "";
    }
  };

  const resolveTime = (timeInput) => {
    const time = new Date(timeInput);

    try {
      const hours = time.getHours();
      const minutes = time.getMinutes();

      const timeStr = `${hours < 10 ? `0${hours}` : hours}:${
        minutes < 10 ? `0${minutes}` : minutes
      }`;

      return timeStr;
    } catch (err) {
      return "";
    }
  };

  const resolveDateObject = (dateObject) => {
    const date = dateObject.date.split("-");
    const time = dateObject.time.split(":");

    const resolvedDate = `${date[2]}-${date[1]}-${date[0]}T${time[0]}:${time[1]}:00`;

    return resolvedDate;
  };

  const fetchCategoryDetail = () => {
    window.$loading(true);
    loading.api = true;
    const categoryId = router.currentRoute.params.id;

    fetchArticleCategoryDetail.payload.Category_Id = categoryId;

    store
      .dispatch("api/apiRequest", fetchArticleCategoryDetail)
      .then((response) => {
        const resData = response.data.service;

        formData.seq = resData.seq;
        formData.category_name_th = resData.category_name_th;
        formData.category_name_en = resData.category_name_en;
        formData.category_image_active.url = resData.category_active_img;
        formData.category_image_inactive.url = resData.category_inactive_img;
        validFrom.date = resolveDate(resData.valid_from);
        validFrom.time = resolveTime(resData.valid_from);
        validThrough.date = resolveDate(resData.valid_through);
        validThrough.time = resolveTime(resData.valid_through);
        formData.status = resData.status == 1;
        loading.api = false;
      })
      .catch((err) => {
      })
      .finally(() => {
        window.$loading(false);
      });
  };

  const createCategory = () => {
    window.$loading(true);
    createArticleCategoryModel.payload.Category_Name_TH =
      formData.category_name_th;
    createArticleCategoryModel.payload.Category_Name_EN =
      formData.category_name_en;
    createArticleCategoryModel.payload.Category_Active_Img =
      formData.category_image_active.file;
    createArticleCategoryModel.payload.Category_Inactive_Img =
      formData.category_image_inactive.file;
    createArticleCategoryModel.payload.Valid_From = resolveDateObject(
      validFrom
    );
    createArticleCategoryModel.payload.Valid_Through = resolveDateObject(
      validThrough
    );
    createArticleCategoryModel.payload.Status = formData.status ? 1 : 0;
    loading.createOrEdit = true;

    store
      .dispatch("api/apiFormRequest", createArticleCategoryModel)
      .then((response) => {
        openSuccessModal(null, null, null, () =>
          router.push("/article/article/category")
        );
      })
      .catch((err) => {
        openDefaultErrorModal(err);
      })
      .finally(() => {
        window.$loading(false);
        loading.createOrEdit = false;
      });
  };

  const editCategory = () => {
    window.$loading(true);
    const categoryId = router.currentRoute.params.id;

    editArticleCategoryModel.payload.Seq = formData.seq;
    editArticleCategoryModel.payload.Category_Id = categoryId;
    editArticleCategoryModel.payload.Category_Name_TH =
      formData.category_name_th;
    editArticleCategoryModel.payload.Category_Name_EN =
      formData.category_name_en;
    editArticleCategoryModel.payload.Category_Active_Img =
      formData.category_image_active.file;
    editArticleCategoryModel.payload.Category_Inactive_Img =
      formData.category_image_inactive.file;
    editArticleCategoryModel.payload.Valid_From = resolveDateObject(validFrom);
    editArticleCategoryModel.payload.Valid_Through = resolveDateObject(
      validThrough
    );
    editArticleCategoryModel.payload.Status = formData.status ? 1 : 0;
    loading.createOrEdit = true;
    store
      .dispatch("api/apiFormRequest", editArticleCategoryModel)
      .then((response) => {
          openSuccessModal(null, null, null, () =>
            router.push("/article/article/category")
          );
      })
      .catch((err) => {
        openDefaultErrorModal(err);
      })
      .finally(() => {
        window.$loading(false);
        loading.createOrEdit = false;
      });
  };

  const onSubmitForm = () => {
    if (props?.isEdit) {
      editCategory();
    } else {
      createCategory();
    }
  };

  onBeforeMount(() => {
    if (props?.isEdit) {
      fetchCategoryDetail();
    }
  });

  return {
    formData,
    loading,
    validFrom,
    validThrough,
    flatpickrDateConfig,
    flatpickrTimeConfig,
    onOpenValidFromDatePicker,
    onOpenValidFromTimePicker,
    onOpenValidThroughTimePicker,
    onSelectValidFromDate,
    onSelectValidFromTimePicker,
    onUploadImageActive,
    onUploadImageInactive,
    onSubmitForm,
  };
}
